import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Textarea } from "../components/ui/textarea";
import { 
  Mic, MicOff, FileText, Clock, CheckCircle2, RefreshCw,
  Play, Pause, Save, Copy, Wand2, AlertCircle, Volume2,
  Brain, ChevronRight, Sparkles, Edit2
} from 'lucide-react';
import { useToast } from "../hooks/use-toast";

const API = process.env.REACT_APP_BACKEND_URL;

const AIScribe = () => {
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [transcript, setTranscript] = useState('');
  const [structuredNote, setStructuredNote] = useState(null);
  const [recentSessions, setRecentSessions] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('visit_note');
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [activeTab, setActiveTab] = useState('record');
  const { toast } = useToast();

  const token = localStorage.getItem('token');

  // Templates for different note types
  const TEMPLATES = [
    { id: 'visit_note', name: 'Visit Note', sections: ['Subjective', 'Objective', 'Assessment', 'Plan'] },
    { id: 'phone_call', name: 'Phone Call', sections: ['Caller', 'Reason', 'Discussion', 'Follow-up'] },
    { id: 'family_meeting', name: 'Family Meeting', sections: ['Attendees', 'Topics Discussed', 'Decisions', 'Next Steps'] },
    { id: 'idg', name: 'IDG Summary', sections: ['Patient Status', 'Team Updates', 'Care Plan Changes', 'Goals'] },
    { id: 'symptom', name: 'Symptom Assessment', sections: ['Symptom', 'Severity', 'Interventions', 'Response'] }
  ];

  // Fetch recent sessions
  const fetchSessions = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/phase45/ai-scribe/sessions`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setRecentSessions(data.sessions || []);
      }
    } catch (err) {
      console.error('Error fetching sessions:', err);
    }
  }, [token]);

  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  // Recording timer
  useEffect(() => {
    let interval;
    if (isRecording) {
      interval = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
    } else {
      setRecordingTime(0);
    }
    return () => clearInterval(interval);
  }, [isRecording]);

  // Format time
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Start/Stop recording
  const toggleRecording = async () => {
    if (isRecording) {
      // Stop recording and process
      setIsRecording(false);
      await processRecording();
    } else {
      // Start recording
      setIsRecording(true);
      setTranscript('');
      setStructuredNote(null);
      
      // Simulate speech recognition (in production, use Web Speech API or Whisper)
      simulateTranscription();
    }
  };

  // Simulate transcription (in production, this would use Whisper API)
  const simulateTranscription = () => {
    const samplePhrases = [
      "Patient is a 78 year old female with end stage heart failure.",
      "Today she reports increased shortness of breath with minimal exertion.",
      "Pain level is 4 out of 10, controlled with current morphine regimen.",
      "Family is present and asking about hospice timeline.",
      "Adjusted comfort medications and provided emotional support.",
      "Will follow up in 2 days to reassess symptoms."
    ];
    
    let phraseIndex = 0;
    const transcriptionInterval = setInterval(() => {
      if (phraseIndex < samplePhrases.length && isRecording) {
        setTranscript(prev => prev + (prev ? ' ' : '') + samplePhrases[phraseIndex]);
        phraseIndex++;
      } else {
        clearInterval(transcriptionInterval);
      }
    }, 3000);
  };

  // Process recording with AI
  const processRecording = async () => {
    if (!transcript.trim()) {
      toast({ title: "No Content", description: "Please record some audio first", variant: "destructive" });
      return;
    }

    setProcessing(true);
    try {
      const res = await fetch(`${API}/api/phase45/ai-scribe/process`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          transcript: transcript,
          template: selectedTemplate
        })
      });

      if (res.ok) {
        const data = await res.json();
        setStructuredNote(data.structured_note);
        toast({ title: "Note Generated", description: "AI has structured your documentation" });
        fetchSessions();
      }
    } catch (err) {
      toast({ title: "Error", description: "Failed to process recording", variant: "destructive" });
    } finally {
      setProcessing(false);
    }
  };

  // Save note to EHR
  const saveToEHR = async () => {
    if (!structuredNote) return;

    try {
      const res = await fetch(`${API}/api/phase45/ai-scribe/save`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          note: structuredNote,
          template: selectedTemplate
        })
      });

      if (res.ok) {
        toast({ title: "Saved to EHR", description: "Note has been saved to patient record" });
      }
    } catch (err) {
      toast({ title: "Error", description: "Failed to save note", variant: "destructive" });
    }
  };

  // Copy to clipboard
  const copyToClipboard = () => {
    if (!structuredNote) return;
    
    const text = Object.entries(structuredNote.sections || {})
      .map(([key, value]) => `${key}:\n${value}`)
      .join('\n\n');
    
    navigator.clipboard.writeText(text);
    toast({ title: "Copied", description: "Note copied to clipboard" });
  };

  return (
    <div className="space-y-6 p-4 md:p-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <Wand2 className="h-7 w-7 text-purple-600" />
            AI Scribe
          </h1>
          <p className="text-gray-600">Voice-to-documentation powered by AI</p>
        </div>
        <Badge className="bg-purple-100 text-purple-700 px-4 py-2">
          <Sparkles className="h-4 w-4 mr-1" />
          GPT-Powered
        </Badge>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-2 w-full max-w-md">
          <TabsTrigger value="record">New Recording</TabsTrigger>
          <TabsTrigger value="history">Recent Notes</TabsTrigger>
        </TabsList>

        {/* Recording Tab */}
        <TabsContent value="record" className="space-y-4">
          {/* Template Selection */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">Note Template</CardTitle>
              <CardDescription>Select the type of documentation</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {TEMPLATES.map(template => (
                  <Button
                    key={template.id}
                    variant={selectedTemplate === template.id ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setSelectedTemplate(template.id)}
                    className={selectedTemplate === template.id ? 'bg-purple-600' : ''}
                  >
                    {template.name}
                  </Button>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Recording Interface */}
          <Card className={isRecording ? 'border-red-300 bg-red-50' : ''}>
            <CardContent className="p-6">
              <div className="text-center space-y-4">
                {/* Recording Button */}
                <button
                  onClick={toggleRecording}
                  disabled={processing}
                  className={`w-24 h-24 rounded-full flex items-center justify-center transition-all ${
                    isRecording 
                      ? 'bg-red-500 hover:bg-red-600 animate-pulse' 
                      : 'bg-purple-600 hover:bg-purple-700'
                  } ${processing ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  {isRecording ? (
                    <MicOff className="h-10 w-10 text-white" />
                  ) : (
                    <Mic className="h-10 w-10 text-white" />
                  )}
                </button>

                {/* Status */}
                <div>
                  {isRecording ? (
                    <div className="space-y-2">
                      <p className="text-red-600 font-medium flex items-center justify-center gap-2">
                        <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                        Recording... {formatTime(recordingTime)}
                      </p>
                      <p className="text-sm text-gray-500">Speak clearly. Click to stop and process.</p>
                    </div>
                  ) : processing ? (
                    <div className="flex items-center justify-center gap-2 text-purple-600">
                      <RefreshCw className="h-4 w-4 animate-spin" />
                      Processing with AI...
                    </div>
                  ) : (
                    <p className="text-gray-500">Click to start recording</p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Live Transcript */}
          {(transcript || isRecording) && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Volume2 className="h-5 w-5 text-teal-600" />
                  Live Transcript
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="min-h-[100px] p-4 bg-gray-50 rounded-lg border">
                  {transcript || (
                    <p className="text-gray-400 italic">Waiting for speech...</p>
                  )}
                  {isRecording && <span className="animate-pulse">|</span>}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Structured Note Output */}
          {structuredNote && (
            <Card className="border-purple-200">
              <CardHeader>
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Brain className="h-5 w-5 text-purple-600" />
                      AI-Structured Note
                    </CardTitle>
                    <CardDescription>
                      {TEMPLATES.find(t => t.id === selectedTemplate)?.name}
                    </CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={copyToClipboard}>
                      <Copy className="h-4 w-4 mr-1" />
                      Copy
                    </Button>
                    <Button size="sm" className="bg-purple-600 hover:bg-purple-700" onClick={saveToEHR}>
                      <Save className="h-4 w-4 mr-1" />
                      Save to EHR
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {Object.entries(structuredNote.sections || {}).map(([section, content]) => (
                  <div key={section} className="space-y-2">
                    <div className="flex items-center justify-between">
                      <h4 className="font-semibold text-gray-800">{section}</h4>
                      <Button variant="ghost" size="sm">
                        <Edit2 className="h-3 w-3" />
                      </Button>
                    </div>
                    <Textarea
                      value={content}
                      onChange={(e) => {
                        setStructuredNote(prev => ({
                          ...prev,
                          sections: {
                            ...prev.sections,
                            [section]: e.target.value
                          }
                        }));
                      }}
                      className="min-h-[80px]"
                    />
                  </div>
                ))}

                {/* AI Suggestions */}
                {structuredNote.suggestions?.length > 0 && (
                  <div className="p-3 bg-purple-50 rounded-lg border border-purple-200">
                    <h4 className="font-medium text-purple-900 mb-2 flex items-center gap-2">
                      <Sparkles className="h-4 w-4" />
                      AI Suggestions
                    </h4>
                    <ul className="space-y-1">
                      {structuredNote.suggestions.map((suggestion, i) => (
                        <li key={i} className="text-sm text-purple-700 flex items-start gap-2">
                          <ChevronRight className="h-4 w-4 mt-0.5" />
                          {suggestion}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* History Tab */}
        <TabsContent value="history" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Recent Scribe Sessions</CardTitle>
              <CardDescription>Your AI-generated notes from the past 7 days</CardDescription>
            </CardHeader>
            <CardContent>
              {recentSessions.length > 0 ? (
                <div className="space-y-3">
                  {recentSessions.map((session, i) => (
                    <div 
                      key={i}
                      className="flex items-center justify-between p-3 border rounded-lg hover:border-purple-300 cursor-pointer"
                    >
                      <div className="flex items-center gap-3">
                        <div className="bg-purple-100 p-2 rounded-lg">
                          <FileText className="h-5 w-5 text-purple-600" />
                        </div>
                        <div>
                          <h4 className="font-medium">{session.title || 'Untitled Note'}</h4>
                          <div className="flex items-center gap-2 text-sm text-gray-500">
                            <Badge variant="outline">{session.template}</Badge>
                            <span>{new Date(session.created_at).toLocaleDateString()}</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {session.saved_to_ehr && (
                          <CheckCircle2 className="h-5 w-5 text-green-500" />
                        )}
                        <ChevronRight className="h-5 w-5 text-gray-400" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Mic className="h-12 w-12 mx-auto text-gray-300 mb-3" />
                  <p className="text-gray-500">No recent sessions</p>
                  <p className="text-sm text-gray-400">Start recording to create your first AI note</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AIScribe;
